// Does the signed-in person belong to a business somebody is paying for?
//
// Asked by the Pro paywall, and by the landing redirect, for people WITHOUT
// Pro. Anyone invited to a paying business uses its books free (Dave,
// 2026-10-01): an AR clerk, an AP clerk or a read-only member should not have
// to buy the personal product to do their job. The owner of a paying business
// already has Pro through the bundled seat, so for owners this answer only
// matters once their business stops paying, and then it is no.
//
// "PAYING" IS THE API'S OWN ANSWER. `access_mode` on each business in
// GET /business/businesses is `full` exactly when the business is paid for
// (Businesses::AccessModeService): its own subscription, a firm paying for it,
// or an owner entitled everywhere. Unpaid books are `read_only` on the server
// whatever this says, so this never decides what anyone may do in them; it
// only decides whether the personal paywall stands in front of them.
//
// A MODULE STORE, NOT A CONTEXT, for the reason firmMembershipStore gives:
// ProtectedRoute and LandingRedirect render above BusinessProvider, which is
// the only thing that otherwise lists businesses. One request per sign-in
// however many callers ask.
//
// A FAILED REQUEST SETTLES AS NO. That shows the paywall, which is what
// everybody without Pro saw before this existed: failing closed costs a
// paying member one reload, failing open would cost nothing on the server but
// would hand the business screens to whoever's request happened to fail.

//
// FIELD STAFF (PRD 89 §4.12, §4.14) are read off the same request: whether any
// business carries a field role (no paywall, and no waitlist, on the business
// screens), and where someone whose every membership is a field role lands.
// One list, so the answers settle together and never cost a second request.
//
// OWNERS (Dave's decision 11, 2026-10-09; gap G46) are read off it too:
// whether the person owns any business, and which business of theirs nobody
// pays for. An owner without Pro reaches that business's billing card past the
// paywall, because the card is the one screen that takes the payment.
//
// READERS OF AN UNPAID BUSINESS (gap G46): an AR or AP clerk or a read-only
// member of a business nobody pays for. The api serves them every GET whatever
// the access mode (PRD 42 Rule 4), so the paywall lets them read it. Not the
// practice roles: without a practice the api refuses them even a GET
// (firm_required), and with one belongsToFirm already opens the books. Not the
// owner, who has the billing card instead.

import { businessService } from './businessService';
import { fieldStaffLanding, isFieldStaff } from '../components/business/fieldRoleLanding';

/** See READERS OF AN UNPAID BUSINESS above. */
const UNPAID_READER_ROLES: ReadonlyArray<string> = ['ar_clerk', 'ap_clerk', 'read_only'];

type Listener = (inPaidBusiness: boolean | null) => void;

interface State {
  uuid: string | undefined;
  inPaidBusiness: boolean | null;
  /** Any business carries a field role; null while unknown. */
  fieldStaff: boolean | null;
  /** Where a field-staff-only user lands; null for anyone else, undefined while unknown. */
  fieldLanding: string | null | undefined;
  /** Owns any business; null while unknown. */
  ownsBusiness: boolean | null;
  /** A business this person owns that is not paid for (`access_mode` not `full`); null if none or unknown. */
  unpaidOwnedBusinessId: string | null;
  /** A clerk or read-only member of a business nobody pays for; null while unknown. */
  readsUnpaidBusiness: boolean | null;
  /** That business; null if none or unknown. */
  unpaidReadBusinessId: string | null;
  inFlight: boolean;
}

export interface ReadsUnpaidBusinessAnswer {
  readsUnpaidBusiness: boolean | null;
  unpaidReadBusinessId: string | null;
}

export interface OwnsBusinessAnswer {
  ownsBusiness: boolean | null;
  unpaidOwnedBusinessId: string | null;
}

const EMPTY: State = {
  uuid: undefined,
  inPaidBusiness: null,
  fieldStaff: null,
  fieldLanding: undefined,
  ownsBusiness: null,
  unpaidOwnedBusinessId: null,
  readsUnpaidBusiness: null,
  unpaidReadBusinessId: null,
  inFlight: false,
};

let state: State = { ...EMPTY };
const listeners = new Set<Listener>();

function publish(): void {
  listeners.forEach((listener) => listener(state.inPaidBusiness));
}

/** The settled answer for this user, or null while it is still unknown. */
export function getInPaidBusiness(uuid: string | undefined): boolean | null {
  return uuid && state.uuid === uuid ? state.inPaidBusiness : null;
}

/** Whether any business carries a field role, or null while it is still unknown. */
export function getFieldStaff(uuid: string | undefined): boolean | null {
  return uuid && state.uuid === uuid ? state.fieldStaff : null;
}

/** §4.12's landing for a field-staff-only user; null for anyone else, undefined while unknown. */
export function getFieldStaffLanding(uuid: string | undefined): string | null | undefined {
  return uuid && state.uuid === uuid ? state.fieldLanding : undefined;
}

/** Whether this person owns a business, and which of theirs is unpaid; ownsBusiness null while unknown. */
export function getOwnsBusiness(uuid: string | undefined): OwnsBusinessAnswer {
  if (!uuid || state.uuid !== uuid) return { ownsBusiness: null, unpaidOwnedBusinessId: null };
  return { ownsBusiness: state.ownsBusiness, unpaidOwnedBusinessId: state.unpaidOwnedBusinessId };
}

/** Whether this person is a clerk or read-only member of an unpaid business, and which. */
export function getReadsUnpaidBusiness(uuid: string | undefined): ReadsUnpaidBusinessAnswer {
  if (!uuid || state.uuid !== uuid) return { readsUnpaidBusiness: null, unpaidReadBusinessId: null };
  return { readsUnpaidBusiness: state.readsUnpaidBusiness, unpaidReadBusinessId: state.unpaidReadBusinessId };
}

export function subscribeInPaidBusiness(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function ensureInPaidBusinessLoaded(uuid: string | undefined): void {
  if (!uuid) return;
  if (state.uuid === uuid && (state.inPaidBusiness !== null || state.inFlight)) return;

  state = { ...EMPTY, uuid, inFlight: true };
  publish();
  ask(uuid);
}

function ask(uuid: string): void {
  businessService
    .listBusinesses()
    .then(({ businesses }) => {
      const owned = businesses.filter((business) => business.role === 'owner');
      const unpaidRead = businesses.find(
        (business) => business.access_mode !== 'full' && UNPAID_READER_ROLES.includes(business.role)
      );
      return {
        inPaidBusiness: businesses.some((business) => business.access_mode === 'full'),
        fieldStaff: isFieldStaff(businesses),
        fieldLanding: fieldStaffLanding(businesses),
        ownsBusiness: owned.length > 0,
        unpaidOwnedBusinessId: owned.find((business) => business.access_mode !== 'full')?.id ?? null,
        readsUnpaidBusiness: unpaidRead !== undefined,
        unpaidReadBusinessId: unpaidRead?.id ?? null,
      };
    })
    // Fails closed, every one: the paywall, as everybody without Pro saw before.
    .catch(() => ({
      inPaidBusiness: false,
      fieldStaff: false,
      fieldLanding: null,
      ownsBusiness: false,
      unpaidOwnedBusinessId: null,
      readsUnpaidBusiness: false,
      unpaidReadBusinessId: null,
    }))
    .then((answer) => {
      // Somebody else signed in while this was in flight: not their answer.
      if (state.uuid !== uuid) return;
      state = { uuid, ...answer, inFlight: false };
      publish();
    });
}

/**
 * Ask again now. For the moments a membership changes under a session that
 * already has an answer: accepting an invitation turns a settled no into a
 * yes, and without this the person who just joined would meet the paywall on
 * the books they were invited to. With no answer held there is nothing to
 * refresh; the next caller asks for itself.
 */
export function invalidateInPaidBusiness(): void {
  const { uuid } = state;
  state = { ...EMPTY };
  ensureInPaidBusinessLoaded(uuid);
}

/**
 * Ask again now, keeping the answer held until the new one arrives. For a
 * change made while the app is on screen (a business purchase landing on the
 * billing card): the paywall waits on a null answer, and its full-page
 * loading would unmount the app under it. With no answer held, as
 * invalidateInPaidBusiness.
 */
export function refreshInPaidBusiness(): void {
  const { uuid, inPaidBusiness } = state;
  if (!uuid) return;
  if (inPaidBusiness === null) {
    invalidateInPaidBusiness();
    return;
  }
  state = { ...state, inFlight: true };
  ask(uuid);
}

/** Tests only. */
export function resetPaidBusinessStore(): void {
  state = { ...EMPTY };
  listeners.clear();
}
